/* Copyright (c) 2020-present CloudIO Inc */

import { RefObject, useEffect } from 'react';
import { useLatest } from '@cloudio-saas/statex';
import handleTouchOrMouseEvent from '../designer/MouseTouchEvent';

export default function useHandleClickOutside<T extends HTMLElement>(
  ref: RefObject<T>,
  callback: (e: MouseEvent | TouchEvent, reason?: 'OutSideClick') => void,
  open = true,
) {
  const callbackRef = useLatest(callback);
  useEffect(() => {
    if (open) {
      const handleClickOutside = (e: MouseEvent | TouchEvent) => {
        const result = handleTouchOrMouseEvent(
          e as unknown as React.MouseEvent<T> | React.TouchEvent<T>,
        );
        if (!result) {
          return;
        }
        const { target } = result;
        if (target) {
          if (ref.current?.contains(target as Node)) {
            return;
          }
          let className = (target as HTMLElement).className;
          if (
            typeof className === 'string' &&
            className.includes('MuiDialog')
          ) {
            // may be a date/time picker inside the popover
            return;
          }
          let parent: HTMLElement | null = (target as HTMLElement)
            .parentElement;
          while (parent) {
            className = parent.className;
            if (
              typeof className === 'string' &&
              className.includes('MuiDialog')
            ) {
              // may be a date/time picker inside the popover
              return;
            }
            parent = parent.parentElement;
          }
        }
        callbackRef.current(e, 'OutSideClick');
      };
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [open, ref, callbackRef]);
}
